import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { chatStoreInstance } from "../stores";
import { handleVibeChatToolResultInvalidations } from "../utils/invalidateSmartTableQueries";

import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { useCancelVibeChat } from "@/queries/vibe/useCancelVibeChat";

const createSendMessageCallbacks = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: number,
) => {
  const invalidateFolderEntities = () => {
    queryClient.invalidateQueries({
      queryKey: ["folder-entities"],
    });
  };

  return {
    onStreamComplete: invalidateFolderEntities,
    onAgentProgress: invalidateFolderEntities,
    onAgentComplete: invalidateFolderEntities,
    onToolResult: (payload: Parameters<typeof handleVibeChatToolResultInvalidations>[2]) => {
      handleVibeChatToolResultInvalidations(queryClient, workspaceId, payload);
    },
  };
};

const parseWorkspaceId = (workspaceId: string | number | undefined): number | null => {
  if (workspaceId === undefined || workspaceId === null) {
    return null;
  }
  return typeof workspaceId === "string" ? parseInt(workspaceId, 10) : workspaceId;
};

export const useQueuedMessageActions = () => {
  const chatStore = chatStoreInstance;
  const workspace = useActiveWorkspace();
  const queryClient = useQueryClient();
  const cancelVibeChatMutation = useCancelVibeChat();

  const tryDrainQueue = useCallback(() => {
    const workspaceId = parseWorkspaceId(workspace?.id);
    if (!workspaceId) {
      return;
    }

    void chatStore.tryDrainMessageQueue(
      workspaceId,
      createSendMessageCallbacks(queryClient, workspaceId),
    );
  }, [chatStore, workspace?.id, queryClient]);

  const sendQueuedImmediately = useCallback(
    async (messageId: string) => {
      const workspaceId = parseWorkspaceId(workspace?.id);
      if (!workspaceId) {
        return;
      }

      const content = chatStore.takeQueuedMessage(messageId);
      if (!content) {
        return;
      }

      const chatId = chatStore.currentChatId;
      if (chatId && chatStore.isLoading) {
        try {
          await cancelVibeChatMutation.mutateAsync({
            sessionId: chatId,
            conversationHistory: chatStore.currentConversationHistory,
          });
        } catch {
          // Still abort locally so the queued message can send.
        }
        chatStore.abortRequest(chatId);
      }

      chatStore.setFollowMode(true);
      await chatStore.handleSendMessage(
        content,
        workspaceId,
        createSendMessageCallbacks(queryClient, workspaceId),
      );
    },
    [chatStore, workspace?.id, cancelVibeChatMutation, queryClient],
  );

  return {
    chatStore,
    sendQueuedImmediately,
    tryDrainQueue,
  };
};
